import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MCQAssessment from './components/MCQAssessment';
import DescriptiveQASection from './components/DescriptiveQASection';
import CodingChallenge from './components/CodingChallenge';
import CompanySpecificPrep from '../interview-preparation-suite/components/CompanySpecificPrep';
import PerformanceAnalytics from '../user-profile-dashboard/components/PerformanceAnalytics';
import MockInterviewScheduler from './components/MockInterviewScheduler';
import { Play, Clock, Code, Users, BarChart3, Building } from 'lucide-react';

const ComprehensiveInterviewSimulator = () => {
  const [activeMode, setActiveMode] = useState('mcq');
  const [isMobile, setIsMobile] = useState(false);
  const [simulatorState, setSimulatorState] = useState({
    totalAssessments: 127,
    avgScore: 85.2,
    interviewsCompleted: 24,
    readinessLevel: 'Advanced',
    weakAreas: ['System Design', 'Database Optimization'],
    strongAreas: ['Test Automation', 'API Testing']
  });

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const assessmentModes = [
    {
      id: 'mcq',
      title: 'Timed MCQ Assessment',
      description: 'Adaptive multiple-choice questions with instant feedback',
      icon: Clock,
      color: 'blue',
      stats: '1,247 questions available'
    },
    {
      id: 'descriptive',
      title: 'Descriptive Q&A',
      description: 'Voice & video responses with AI-powered analysis',
      icon: Users,
      color: 'purple',
      stats: 'Voice/Video recording enabled'
    },
    {
      id: 'coding',
      title: 'Live Coding Challenge',
      description: 'Real-time code execution with automated validation',
      icon: Code,
      color: 'green',
      stats: '15+ programming languages'
    },
    {
      id: 'company',
      title: 'Company-Specific Prep',
      description: 'Tailored questions from top tech companies',
      icon: Building,
      color: 'orange',
      stats: '50+ company databases'
    },
    {
      id: 'analytics',
      title: 'Performance Analytics',
      description: 'Detailed insights and improvement recommendations',
      icon: BarChart3,
      color: 'indigo',
      stats: 'AI-powered feedback'
    },
    {
      id: 'scheduler',
      title: 'Mock Interview Scheduler',
      description: 'Schedule practice sessions with mentors',
      icon: Play,
      color: 'red',
      stats: 'Peer & mentor sessions'
    }
  ];

  const getColorClasses = (color, isActive = false) => {
    const colors = {
      blue: isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100',
      purple: isActive ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-600 hover:bg-purple-100',
      green: isActive ? 'bg-green-600 text-white' : 'bg-green-50 text-green-600 hover:bg-green-100',
      orange: isActive ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-600 hover:bg-orange-100',
      indigo: isActive ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100',
      red: isActive ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'
    };
    return colors?.[color];
  };

  const renderModeContent = () => {
    switch (activeMode) {
      case 'mcq':
        return <MCQAssessment simulatorState={simulatorState} />;
      case 'descriptive':
        return <DescriptiveQASection simulatorState={simulatorState} />;
      case 'coding':
        return <CodingChallenge simulatorState={simulatorState} />;
      case 'company':
        return <CompanySpecificPrep simulatorState={simulatorState} />;
      case 'analytics':
        return <PerformanceAnalytics analyticsData={simulatorState} simulatorState={simulatorState} />;
      case 'scheduler':
        return <MockInterviewScheduler simulatorState={simulatorState} />;
      default:
        return <MCQAssessment simulatorState={simulatorState} />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Comprehensive Interview Simulator - Testing University</title>
        <meta 
          name="description" 
          content="Advanced interview preparation through multi-format assessments including timed MCQs, descriptive Q&A, and technical coding challenges with AI-powered feedback." 
        />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <Header />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Comprehensive Interview Simulator
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-indigo-100 max-w-4xl mx-auto">
                Advanced multi-format interview preparation with AI-powered feedback, real-time coding challenges, 
                and company-specific question databases
              </p>
              
              {/* Quick Stats Dashboard */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold mb-2">{simulatorState?.totalAssessments}</div>
                  <div className="text-sm text-indigo-100">Assessments Taken</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold mb-2">{simulatorState?.avgScore}%</div>
                  <div className="text-sm text-indigo-100">Average Score</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold mb-2">{simulatorState?.interviewsCompleted}</div>
                  <div className="text-sm text-indigo-100">Mock Interviews</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold mb-2">{simulatorState?.readinessLevel}</div>
                  <div className="text-sm text-indigo-100">Readiness Level</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Assessment Mode Selection */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-3xl font-bold text-center mb-12">Choose Your Assessment Format</h2>
          
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'} gap-6 mb-12`}>
            {assessmentModes?.map((mode) => {
              const IconComponent = mode?.icon;
              const isActive = activeMode === mode?.id;
              
              return (
                <div
                  key={mode?.id}
                  onClick={() => setActiveMode(mode?.id)}
                  className={`
                    p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-105
                    ${isActive ? 'border-indigo-500 shadow-lg scale-105' : 'border-gray-200 hover:border-indigo-300'}
                    ${getColorClasses(mode?.color, isActive)}
                  `}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <IconComponent 
                      className={`w-8 h-8 ${isActive ? 'text-white' : `text-${mode?.color}-600`}`} 
                    />
                    <h3 className="text-lg font-semibold">{mode?.title}</h3>
                  </div>
                  
                  <p className={`text-sm mb-4 ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
                    {mode?.description}
                  </p>
                  
                  <div className={`text-xs font-medium ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                    {mode?.stats}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active Mode Content */}
          <div className="min-h-[600px]">
            {renderModeContent()}
          </div>
        </div>

        {/* Strengths & Weaknesses Summary */}
        <div className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Strong Areas */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-green-700">🎯 Your Strong Areas</h3>
                <div className="space-y-2">
                  {simulatorState?.strongAreas?.map((area, index) => (
                    <div key={index} className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium">
                      {area}
                    </div>
                  ))}
                </div>
              </div>

              {/* Improvement Areas */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-orange-700">🎓 Focus Areas</h3>
                <div className="space-y-2">
                  {simulatorState?.weakAreas?.map((area, index) => (
                    <div key={index} className="bg-orange-50 text-orange-700 px-3 py-2 rounded-lg text-sm font-medium">
                      {area}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Floating Action */}
        {isMobile && (
          <div className="fixed bottom-6 right-6 z-50">
            <button 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
              onClick={() => setActiveMode('mcq')}
            >
              <Play className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ComprehensiveInterviewSimulator;